import { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import SideSheetModal from 'shared/components/SideSheetModal'
import { colors, fonts, media } from 'shared/lib'
import { RaffleDetail } from 'shared/types/raffles'
import { FC_STORES } from 'shared/constants/raffleStores'
import { t } from 'localization'
import { useRaffleNotifyForm } from 'shared/hooks/useRaffleNotifyForm'

const TP = 'shared.components.Raffles.RaffleNotifyModal'

type RaffleNotifyModalProps = {
  /**
   * Optional raffle detail. When provided, the mobile hero banner shows
   * the raffle's brand + product name and the store list defaults to
   * `raffle.stores`. When omitted (e.g. for a generic email-signup
   * module on /raffles), the hero banner is hidden and the explicit
   * `stores` prop is used instead.
   */
  raffle?: RaffleDetail
  /**
   * Explicit list of store cities (keys of `FC_STORES`) to render in
   * the picker. Falls back to `raffle.stores` when not provided.
   */
  stores?: string[]
  /**
   * Source surface for analytics tracking. Defaults to `detail_page`
   * when a raffle is provided, or `index_sidebar` otherwise.
   */
  trackingSource?: 'detail_page' | 'index_sidebar' | 'pdp_panel'
  onClose: () => void
}

export const RaffleNotifyModal = ({
  raffle,
  stores,
  trackingSource,
  onClose,
}: RaffleNotifyModalProps) => {
  const resolvedSource = trackingSource ?? (raffle ? 'detail_page' : 'index_sidebar')
  const {
    selectedStore,
    setSelectedStore,
    email,
    setEmail,
    emailError,
    setEmailError,
    successMessage,
    setSuccessMessage,
    errorMessage,
    setErrorMessage,
    isLoading,
    validateEmail,
    handleSubmit,
  } = useRaffleNotifyForm({
    translationPrefix: TP,
    submitErrorMessage: t(`${TP}.submitError`, 'Something went wrong. Please try again.'),
    trackingSource: resolvedSource,
    trackingProductTemplateSlug: (raffle?.productSlug || raffle?.slug) ?? null,
  })

  const availableStores = useMemo(() => {
    const storeCities = stores ?? raffle?.stores ?? []
    return storeCities
      .map((city) => ({ city, ...FC_STORES[city] }))
      .filter((store) => store.id)
  }, [stores, raffle?.stores])

  // Auto-select when single store
  useEffect(() => {
    if (availableStores.length === 1 && !selectedStore) {
      setSelectedStore(availableStores[0].city)
    }
  }, [availableStores, selectedStore, setSelectedStore])

  const isDisabled = !selectedStore || !email || !!emailError || isLoading

  return (
    <SideSheetModal
      isOpen
      animated
      anchor="right"
      mobileAnchor="bottom"
      mobileFullScreen
      overlayVariant="dark"
      hasPadding={false}
      noBorder
      onClose={onClose}
    >
      <ModalInner data-qa="raffle-notify-modal">
        {raffle && (
          <HeroBanner>
            <HeroBrand>{raffle.brand}</HeroBrand>
            <HeroProductName>{raffle.productName}</HeroProductName>
          </HeroBanner>
        )}

        <ModalHeader>
          <HeaderSide />
          <HeaderTitle id="notify-modal-title">
            {t(`${TP}.notifyMe`, 'Notify Me')}
          </HeaderTitle>
          <HeaderSide>
            <CloseButton
              onClick={onClose}
              data-qa="raffle-notify-close"
              aria-label={t(`${TP}.closeModal`, 'Close modal')}
            >
              ✕
            </CloseButton>
          </HeaderSide>
        </ModalHeader>

        <ScrollContent>
          <StoresSection role="radiogroup" aria-labelledby="store-selection-title">
            <SectionTitle id="store-selection-title">
              {t(`${TP}.selectStoreDescription`, "Select your preferred Flight Club pickup location and enter your email to be notified when new drawings are added or open for entry.")}
            </SectionTitle>
            {availableStores.map((store) => (
              <StoreCard
                key={store.id}
                $selected={selectedStore === store.city}
                onClick={() => setSelectedStore(store.city)}
                data-qa={`notify-store-${store.id}`}
                role="radio"
                aria-checked={selectedStore === store.city}
                aria-label={`${t(`shared.components.Raffles.stores.${store.id}.name`, store.name)}, ${t(`shared.components.Raffles.stores.${store.id}.address`, store.address)}`}
              >
                <StoreName>{t(`shared.components.Raffles.stores.${store.id}.name`, store.name)}</StoreName>
                <StoreAddress>{t(`shared.components.Raffles.stores.${store.id}.address`, store.address)}</StoreAddress>
              </StoreCard>
            ))}
          </StoresSection>

          <Divider />

          <EmailSection>
            <EmailInput
              id="notify-email"
              type="email"
              placeholder={t(`${TP}.emailPlaceholder`, 'Email Address')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError('')
                if (successMessage) setSuccessMessage('')
                if (errorMessage) setErrorMessage('')
              }}
              onBlur={() => email && validateEmail(email)}
              data-qa="raffle-notify-email"
              aria-label={t(`${TP}.emailLabel`, 'Email address')}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'email-error' : undefined}
            />
          </EmailSection>

          {successMessage && (
            <SuccessText role="status" aria-live="polite">
              {successMessage}
            </SuccessText>
          )}
          {errorMessage && (
            <ErrorText role="alert" aria-live="assertive">
              {errorMessage}
            </ErrorText>
          )}
        </ScrollContent>

        <Footer>
          <NotifyButton
            onClick={handleSubmit}
            disabled={isDisabled}
            data-qa="raffle-notify-submit"
            aria-label={t(
              `${TP}.submitLabel`,
              'Submit notification signup',
            )}
          >
            {isLoading
              ? t(`${TP}.submitting`, 'Submitting...')
              : t(`${TP}.getNotified`, 'Notify Me')}
          </NotifyButton>
          <DisclaimerText>
            {t(`${TP}.newsletterDisclaimer`, "By proceeding, you agree to receive Flight Club's email newsletter with special promotions, news and more.")}
          </DisclaimerText>
        </Footer>
      </ModalInner>
    </SideSheetModal>
  )
}

// Styled components
const ModalInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${colors.FC2_WHITE};
  overflow: hidden;
  min-height: 0;
`

const HeroBanner = styled.div`
  background-color: ${colors.FC2_BLACK};
  color: ${colors.FC2_WHITE};
  padding: 24px 16px;
  text-align: center;
  ${media.large`display: none;`}
`

const HeroBrand = styled.div`
  ${fonts.SUBTITLE_3}
  text-transform: uppercase;
  margin-bottom: 4px;
`

const HeroProductName = styled.div`
  ${fonts.SUBTITLE_2}
`

const ModalHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
  flex-shrink: 0;
  background: ${colors.FC2_WHITE};
`

const HeaderSide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const HeaderTitle = styled.h2`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0;
  color: ${colors.FC2_BLACK};
  margin: 0;
  text-align: center;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: ${colors.FC2_BLACK};
  padding: 0;
  width: 32px;
  height: 32px;
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const StoresSection = styled.div`
  padding: 32px 24px;
`

const Divider = styled.div`
  height: 1px;
  background: ${colors.FC2_LIGHTEST_GREY};
  width: 100%;
`

const EmailSection = styled.div`
  padding: 32px 24px;
`

const SectionTitle = styled.h3`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: ${colors.FC2_GREY};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  text-transform: none;
  letter-spacing: 0;
  margin: 0 0 24px;
`

const StoreCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  height: 64px;
  padding: 16px;
  border-radius: 2px;
  background: ${({ $selected }) => ($selected ? colors.FC2_WHITE : colors.FC2_OFF_WHITE)};
  border: ${({ $selected }) => ($selected ? `1.5px solid ${colors.FC2_BLACK}` : `0.5px solid ${colors.FC2_DISABLED_GREY}`)};
  cursor: pointer;
  text-align: left;
  transition: background-color 100ms ease, border 100ms ease;
  margin-bottom: 12px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const StoreName = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: ${colors.FC2_BLACK};
`

const StoreAddress = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3;
  color: ${colors.FC2_GREY};
`

const EmailInput = styled.input`
  background: ${colors.FC2_WHITE};
  border: 1px solid ${colors.FC2_DISABLED_GREY};
  border-radius: 4px;
  padding: 12px 16px;
  height: 56px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.FC2_GREY};
  width: 100%;
  box-sizing: border-box;
  margin: 0;

  &:focus {
    outline: none;
    border-color: ${colors.FC2_BLACK};
  }
`

const DisclaimerText = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: ${colors.FC2_DARK_GREY};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  margin: 16px 0 0;
  padding: 0;
  text-align: center;
`

const SuccessText = styled.p`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_BLACK};
  padding: 0 24px;
`

const ErrorText = styled.p`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_RED};
  padding: 0 24px;
`

const Footer = styled.div`
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  padding: 16px;
`

const NotifyButton = styled.button`
  background: ${colors.FC2_BLACK};
  height: 56px;
  border-radius: 1px;
  width: 100%;
  color: ${colors.FC2_WHITE};
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
